import { useCallback } from "react"
import {
  Avatar,
  Box,
  Button,
  Card,
  Typography,
  useTheme,
  Stack,
} from "@mui/material"
import { Link, useNavigate } from "react-router-dom"
import { WelcomePanel } from "components/WelcomePanel"
import { Head } from "components/Head"
import { useTranslation } from "providers/i18n"
import { FullScreenOverlayLayout } from "layouts/FullScreenOverlayLayout"
import { Header, HeaderLogo, HeaderTitle } from "components/Header"
import { Clock } from "components/Clock"
import { Footer } from "components/Footer"
import { getFeatureName } from "utils/feature"
import { Carousel, CarouselItem } from "components/Carousel"
import { HighlightFeatureCard } from "components/domains/events"
import { useHome } from "../../hooks/home/useHome"
import { FullScreenStart } from "components/domains/home"

export const HomePage = ({ navigations }) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { featuredEventPromotion, defaultItemsLinks, venues, locales } =
    useHome()

  const styleOverrides = theme.components[`HomePage`]?.styleOverrides || {}
  const {
    root,
    contentContainer,
    featureCarouselWrapper,
    headerWrapper,
    footerWrapper,
    welcomePanelWrapper,
    carouselWrapper,
    welcomeContainer,
    greetingText,
    localeListContainer,
    localeButton,
    actionContainer,
    actionButton,
    textCard,
    linkCardContainer,
    defaultCardPrimaryText,
    defaultCardSecondaryText,
    defaultCardIcon,
  } = styleOverrides

  const handleClickNav = useCallback(
    (to) => {
      navigate(to)
    },
    [navigate]
  )

  const switchLanguage = useCallback(
    (lang) => (e) => {
      i18n.changeLanguage(lang)
    },
    [i18n]
  )

  return (
    <>
      <Head title={t("Indoor map")} />
      <Box sx={headerWrapper}>
        <Header
          logo={<HeaderLogo />}
          title={<HeaderTitle />}
          clock={<Clock />}
        />
      </Box>
      <FullScreenOverlayLayout sx={root}>
        <Box sx={contentContainer}>
          <FullScreenStart />
          <Box sx={welcomePanelWrapper}>
            <WelcomePanel
              navigations={navigations}
              onClickNav={handleClickNav}
            />
          </Box>
          <Box sx={carouselWrapper}></Box>
          <Box sx={welcomeContainer}>
            <Typography variant="h3" sx={greetingText}>
              {t("Welcome to", { name: t(getFeatureName(venues[0])) })}
            </Typography>
            <Box sx={localeListContainer}>
              {locales.map((locale) => (
                <Button
                  key={locale.localeKey}
                  onClick={switchLanguage(locale.localeKey)}
                  sx={localeButton}
                >
                  <Avatar
                    src={`https://flagcdn.com/h40/${locale.flagCode}.webp`}
                  />
                </Button>
              ))}
            </Box>
            <Box sx={actionContainer}>
              <Button
                sx={actionButton}
                component={Link}
                to={"/maps"}
                variant="contained"
              >
                {t("Touch to start")}
              </Button>
            </Box>
          </Box>
        </Box>
        <Box sx={featureCarouselWrapper}>
          <Carousel
            options={{
              gap: theme.spacing(1.25),
              pagination: false,
              focus: "center",
              arrows: false,
              mediaQuery: "min",
              breakpoints: {
                640: {
                  perPage: 2,
                },
                900: {
                  perPage: 3,
                },
              },
            }}
          >
            {featuredEventPromotion.map((feature) => {
              const { feature_type, id } = feature
              return (
                <CarouselItem key={id}>
                  {/*
                   * @Example: Event => events/event-id, Promotion => promotions/promotion-id
                   */}
                  <Link to={`${feature_type}s/${id}`}>
                    <HighlightFeatureCard feature={feature} />
                  </Link>
                </CarouselItem>
              )
            })}
            {defaultItemsLinks && defaultItemsLinks.length > 0 ? (
              <CarouselItem>
                <Box sx={linkCardContainer}>
                  {defaultItemsLinks.map(
                    ({ to, primaryText, secondaryText, icon }) => (
                      <Link key={to} to={to}>
                        <Card sx={textCard}>
                          <Stack spacing={1}>
                            <Box sx={defaultCardIcon}>{icon}</Box>
                            <Stack spacing={0.5}>
                              <Typography
                                variant="title"
                                sx={defaultCardPrimaryText}
                              >
                                {t(primaryText)}
                              </Typography>
                              <Typography
                                variant="subTitle"
                                sx={defaultCardSecondaryText}
                              >
                                {t(secondaryText)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Card>
                      </Link>
                    )
                  )}
                </Box>
              </CarouselItem>
            ) : null}
          </Carousel>
        </Box>
      </FullScreenOverlayLayout>
      <Box sx={footerWrapper}>
        <Footer />
      </Box>
    </>
  )
}
